import { ComponentRef, createComponent, EnvironmentInjector, Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { PrintBackdropComponent } from '../content/print-backdrop/print-backdrop.component';
import { CustomToastersService } from './custom-toasters.service';
import { DownloadFileService } from './download-file.service';
import { ResponsiveService } from './responsive.service';

@Injectable()
export class PrintPdfService {
  private objectUrl: string;
  private iframe: HTMLIFrameElement;
  private backdropComponentRef: ComponentRef<PrintBackdropComponent>;

  constructor(
    private readonly customToasters: CustomToastersService,
    private readonly responsiveService: ResponsiveService,
    private readonly downloadFileService: DownloadFileService,
    private readonly environmentInjector: EnvironmentInjector
  ) {
    this.objectUrl = null;
    this.iframe = null;
    this.backdropComponentRef = null;
  }

  public print(observable: Observable<Blob>, title: string, body: string, fileName: string): Subscription {
    this.customToasters.waitLoad();
    this.showBackdrop(title, body);

    const subscription = observable.subscribe(
      (response) => {
        if (this.responsiveService.isDesktopDevice && !this.responsiveService.isIe) {
          this.performPrint(response);
        } else {
          this.performDownload(response, fileName);
        }
      },
      () => {
        this.customToasters.failLoad();
        this.hideBackdrop();
      }
    );

    return subscription;
  }

  public dispose(): void {
    if (this.iframe !== null) {
      URL.revokeObjectURL(this.objectUrl);

      document.body.removeChild(this.iframe);

      this.objectUrl = null;
      this.iframe = null;
    }

    if (this.backdropComponentRef !== null) {
      this.hideBackdrop();
    }
  }

  private showBackdrop(title: string, body: string): void {
    this.backdropComponentRef = createComponent(PrintBackdropComponent, {
      environmentInjector: this.environmentInjector
    });

    this.backdropComponentRef.instance.title = title;
    this.backdropComponentRef.instance.body = body;

    document.body.appendChild(this.backdropComponentRef.location.nativeElement);

    this.backdropComponentRef.changeDetectorRef.detectChanges();
  }

  private hideBackdrop(): void {
    this.backdropComponentRef.destroy();

    document.body.removeChild(this.backdropComponentRef.location.nativeElement);

    this.backdropComponentRef = null;
  }

  private performPrint(response: Blob): void {
    if (this.iframe !== null) {
      URL.revokeObjectURL(this.objectUrl);
      document.body.removeChild(this.iframe);
    }

    this.iframe = document.createElement('iframe');

    this.iframe.style.display = 'none';

    this.iframe.addEventListener('load', () => {
      setTimeout(() => {
        this.iframe.focus();
        this.iframe.contentWindow.print();
      }, 1000);
    });

    document.body.appendChild(this.iframe);

    this.objectUrl = URL.createObjectURL(response);

    this.iframe.src = this.objectUrl;

    setTimeout(() => {
      this.customToasters.clear();
      this.hideBackdrop();
    }, 1000);
  }

  private performDownload(response: Blob, fileName: string): void {
    this.customToasters.clear();
    this.hideBackdrop();
    this.downloadFileService.save(response, `${fileName}.pdf`);
  }
}
